import slugify from "slugify";
import { nextTick } from "vue";

import { LogService } from "@/services";
import { AppConfigDistrictResponse, VignetteRoutes } from "@/types";
import { useSearchStore } from "@/pinia/search";
import router from "@/router/index";

declare global {
  interface Window {
    // eslint-disable-next-line @typescript-eslint/ban-types
    dataLayer: object[];
    // dataLayer: Record<string, unknown>;
  }
}

export class VignetteHelpers {
  static focusToElement(elementId: string): void {
    const element = document.getElementById(elementId);
    if (element) {
      LogService.info(
        `VignetteHelpers.focusToElement: Scrolling to element with id: ${elementId}`
      );
      nextTick(() => element.scrollIntoView({ behavior: "smooth" }));
    } else {
      LogService.info(
        `VignetteHelpers.focusToElement: element with id: ${elementId} not found.`
      );
    }
  }

  static slugify(text: string): string {
    return slugify(text, {
      lower: true,
      remove: /[>%_\\$~&+,<:;/=?@#`|'.^*()!\[\]{}"-]/g,
    });
  }

  static getProfileNameSlug(profileName: string): string {
    return "-" + VignetteHelpers.slugify(profileName);
  }

  static getRouteFromSearchItemType(type: string): VignetteRoutes {
    type VignetteRouteType =
      | "Caucus"
      | "Committee"
      | "Profile"
      | "ProfileKW"
      | "Agency"
      | "State"
      | "District";
    if (!type) return VignetteRoutes.Home;
    let prep;
    if (["agencyperson", "person", "staffer"].includes(type)) {
      prep = "ProfileKW" as VignetteRouteType;
    } else {
      prep = (type.charAt(0).toUpperCase() +
        type.slice(1)) as VignetteRouteType;
    }
    return VignetteRoutes[prep];
  }

  static scrollToResults(): void {
    const searchStore = useSearchStore();
    searchStore.$patch({ searchFormIsHidden: true });
    nextTick(() => {
      const e = document.getElementById("search-results-header");
      if (e) {
        e.scrollIntoView();
      }
    });
  }

  static goToDistrict(d: AppConfigDistrictResponse): void {
    let name = d.name;
    if (d.name.includes(" (")) {
      name = name.substring(0, d.name.indexOf(" ("));
    }
    const slugged = VignetteHelpers.slugify(name);
    router.push({
      name: VignetteRoutes.District,
      params: {
        id: d.legdistrictid + "",
        name: "-" + slugged,
      },
    });
  }

  /**
   * This helper function's goal is simply to return the correct base origin when routing to a django provided site.
   * It is very useful when developing on local host and need to test links originating from
   * Vignette/Almanac+ directing towards Almanac. I.E... :8080 --> :8001
   */
  static getBaseOriginForDjangoSite() {
    let baseOrigin = window.location.origin;
    LogService.debug(`baseUrlOrigin: '${baseOrigin}`);
    // return correct redirect login url depending on the host: localhost --> localhost:8001 (for vignette since dev is
    // :8080), stage.njdc.com -> state..., prod -> nationaljournal.com
    // current baseOrigin includes port number so need to strip it in the case of vignette dev and point to django port
    const hostname = window.location.hostname;
    const isOnLocal =
      ["localhost", "127.0.0.1"].includes(hostname) ||
      hostname.startsWith("192.168.") ||
      hostname.startsWith("172.16.") ||
      hostname.startsWith("10.") ||
      hostname.endsWith(".local");
    if (isOnLocal) {
      baseOrigin = `http://${window.location.hostname}:8001`;
    }
    return baseOrigin;
  }

  static Analytics = class {
    // eslint-disable-next-line @typescript-eslint/ban-types
    static push(item: object): void {
      LogService.info(`Analytics - sending new metadata`);
      window.dataLayer.push(item);
    }

    static pushEvent(eventName: string): void {
      LogService.info(`Analytics - sending event beacon: '${eventName}'`);
      window.dataLayer.push({
        event: eventName,
      });
    }
  };

  static cleanFloat(value: string | null | undefined, decimalPlaces = 0) {
    if (value !== undefined && value !== null) {
      // Convert value to string
      value = value.toString();

      // Remove any non-numeric characters except . and -
      const stripped = value.replace(/[^0-9.-]/g, "");

      // Convert to float
      let num = parseFloat(stripped);

      // Check if the number is valid
      if (isNaN(num)) {
        return "";
      }

      // Only divide by 100 if the number is clearly too large to be a direct percentage
      if (num > 100) {
        num /= 100;
      }

      // Format to specified decimal places
      return num.toFixed(decimalPlaces);
    }
    return "";
  }
}
